import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Clock, Users, Calendar, Ticket } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';
import { resolveImageUrl } from '../lib/imageUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Classes = () => {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [payMethod, setPayMethod] = useState('pay');
  const [passCode, setPassCode] = useState('');
  const [terms, setTerms] = useState({ payment: false, media: false, property: false, health: false });
  const [bookingData, setBookingData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    booking_date: '',
    notes: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`);
      setClasses(response.data);
    } catch (error) {
      console.error('Error fetching classes:', error);
      toast.error('Failed to load classes');
    }
  };

  const handleBookClass = (danceClass) => {
    setSelectedClass(danceClass);
    setPayMethod('pay');
    setPassCode('');
    setTerms({ payment: false, media: false, property: false, health: false });
    if (danceClass.class_date) {
      setBookingData((p) => ({ ...p, booking_date: danceClass.class_date }));
    }
  };

  const handleInputChange = (e) => {
    setBookingData({ ...bookingData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!bookingData.customer_name || !bookingData.customer_email || !bookingData.customer_phone || !bookingData.booking_date) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (payMethod === 'pass' && !passCode.trim()) {
      toast.error('Please enter your class pass code');
      return;
    }
    if (!(terms.payment && terms.media && terms.property && terms.health)) {
      toast.error('Please acknowledge and agree to all booking terms');
      return;
    }

    try {
      const payload = {
        ...bookingData,
        booking_type: 'class',
        class_id: selectedClass.id,
        booking_time: selectedClass.time,
      };
      if (payMethod === 'pass') payload.pass_code = passCode.trim().toUpperCase();

      const response = await axios.post(`${API}/bookings`, payload);

      if (payMethod === 'pass') {
        toast.success('Class pass applied! Your slot is confirmed.');
        setTimeout(() => navigate(`/confirmation/${response.data.id}`), 800);
      } else {
        toast.success('Booking created! Redirecting to payment...');
        setTimeout(() => navigate(`/payment/${response.data.id}`), 1000);
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to create booking');
    }
  };

  const formatDate = (d) =>
    new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  const formatTime = (t) => {
    if (!t || !t.includes(':')) return t;
    const [h, m] = t.split(':').map(Number);
    if (Number.isNaN(h)) return t;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hr = h % 12 || 12;
    return `${hr}:${String(m || 0).padStart(2, '0')} ${ampm}`;
  };

  const buildGroups = (classes) => {
    const dated = classes.filter((c) => c.class_date);
    const undated = classes.filter((c) => !c.class_date);
    const groups = [];
    const seen = {};
    // Classes already arrive sorted by date+time from the API.
    dated.forEach((c) => {
      if (!seen[c.class_date]) {
        seen[c.class_date] = [];
        groups.push({ key: c.class_date, label: formatDate(c.class_date), items: seen[c.class_date] });
      }
      seen[c.class_date].push(c);
    });
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    days.forEach((day) => {
      const items = undated.filter((c) => c.day_of_week === day);
      if (items.length) groups.push({ key: day, label: day, items });
    });
    return groups;
  };

  const groups = buildGroups(classes);

  return (
    <div className="min-h-screen pt-32 pb-16">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6" data-testid="classes-header">
          <div>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tighter mb-4" style={{ fontFamily: 'var(--heading-font)' }}>
              DANCE CLASSES
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-zinc-600">
              Join our weekly classes and elevate your dance journey
            </p>
          </div>
          <Link to="/class-passes" className="shrink-0">
            <button
              className="w-full md:w-auto bg-black text-white hover:bg-zinc-800 rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
              data-testid="purchase-class-pass-btn"
            >
              <Ticket size={18} /> PURCHASE A CLASS PASS
            </button>
          </Link>
        </div>

        {/* Schedule grouped by date / day */}
        <div className="space-y-12">
          {groups.map((group) => (
            <div key={group.key} data-testid={`schedule-group-${group.key}`}>
                <h2 className="text-3xl font-bold mb-6 border-b-2 border-black pb-2" style={{ fontFamily: 'var(--heading-font)' }}>
                  {group.label}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {group.items.map((danceClass) => (
                    <div
                      key={danceClass.id}
                      className="bg-white border border-zinc-100 overflow-hidden hover:border-black transition-all duration-300 studio-card"
                      data-testid={`class-card-${danceClass.id}`}
                    >
                      <div className="aspect-[3/4] bg-cover bg-center" style={{ backgroundImage: `url('${resolveImageUrl(danceClass.image_url)}')` }} />
                      <div className="p-6">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-medium uppercase tracking-widest text-zinc-400">
                            {(danceClass.levels && danceClass.levels.length ? danceClass.levels : [danceClass.level]).filter(Boolean).join(' • ')}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'var(--heading-font)' }}>
                          {danceClass.name}
                        </h3>
                        <p className="text-sm text-zinc-600 mb-4 line-clamp-2">
                          {danceClass.description}
                        </p>
                        
                        <div className="space-y-2 mb-4 text-sm">
                          <div className="flex items-center gap-2 text-zinc-700">
                            <Clock size={16} />
                            <span>{formatTime(danceClass.time)} • {danceClass.duration} mins</span>
                          </div>
                          <div className="flex items-center gap-2 text-zinc-700">
                            <Users size={16} />
                            <span>Max {danceClass.capacity} students</span>
                          </div>
                          <div className="text-zinc-700">
                            <strong>Instructor:</strong> {danceClass.instructor}
                          </div>
                          <div className="text-zinc-900 font-bold text-base" data-testid={`class-price-${danceClass.id}`}>
                            ₱{Number(danceClass.price || 0).toLocaleString()}
                          </div>
                        </div>

                        {danceClass.sold_out ? (
                          <button
                            disabled
                            className="w-full bg-zinc-200 text-zinc-500 rounded-full px-6 py-3 font-bold cursor-not-allowed"
                            data-testid={`sold-out-btn-${danceClass.id}`}
                          >
                            SOLD OUT
                          </button>
                        ) : (
                          <button
                            onClick={() => handleBookClass(danceClass)}
                            className="w-full bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95"
                            data-testid={`book-class-btn-${danceClass.id}`}
                          >
                            BOOK NOW
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
            </div>
          ))}
        </div>
      </div>

      {/* Booking Dialog */}
      <Dialog open={!!selectedClass} onOpenChange={() => setSelectedClass(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'var(--heading-font)' }}>
              Book {selectedClass?.name}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="customer_name">Full Name *</Label>
              <Input
                id="customer_name"
                name="customer_name"
                value={bookingData.customer_name}
                onChange={handleInputChange}
                required
                data-testid="booking-name-input"
              />
            </div>

            <div>
              <Label htmlFor="customer_email">Email *</Label>
              <Input
                id="customer_email"
                name="customer_email"
                type="email"
                value={bookingData.customer_email}
                onChange={handleInputChange}
                required
                data-testid="booking-email-input"
              />
            </div>

            <div>
              <Label htmlFor="customer_phone">Phone Number *</Label>
              <Input
                id="customer_phone"
                name="customer_phone"
                value={bookingData.customer_phone}
                onChange={handleInputChange}
                required
                data-testid="booking-phone-input"
              />
            </div>

            <div>
              <Label htmlFor="booking_date">{selectedClass?.class_date ? 'Class Date' : 'Select Date *'}</Label>
              <Input
                id="booking_date"
                name="booking_date"
                type="date"
                value={bookingData.booking_date}
                onChange={handleInputChange}
                min={new Date().toISOString().split('T')[0]}
                required
                readOnly={!!selectedClass?.class_date}
                disabled={!!selectedClass?.class_date}
                data-testid="booking-date-input"
              />
              {selectedClass?.class_date && (
                <p className="text-xs text-zinc-500 mt-1">This class runs on a fixed date.</p>
              )}
            </div>

            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Input
                id="notes"
                name="notes"
                value={bookingData.notes}
                onChange={handleInputChange}
                data-testid="booking-notes-input"
              />
            </div>

            {/* Payment method */}
            <div>
              <Label>Payment Method</Label>
              <div className={`grid ${selectedClass?.is_collab ? 'grid-cols-1' : 'grid-cols-2'} gap-2 mt-1`}>
                <button
                  type="button"
                  onClick={() => setPayMethod('pay')}
                  className={`rounded-full px-4 py-2 text-sm font-medium border-2 transition-colors ${payMethod === 'pay' ? 'border-black bg-black text-white' : 'border-zinc-300 text-zinc-600'}`}
                  data-testid="paymethod-pay"
                >
                  GCash / Bank
                </button>
                {!selectedClass?.is_collab && (
                  <button
                    type="button"
                    onClick={() => setPayMethod('pass')}
                    className={`rounded-full px-4 py-2 text-sm font-medium border-2 transition-colors ${payMethod === 'pass' ? 'border-black bg-black text-white' : 'border-zinc-300 text-zinc-600'}`}
                    data-testid="paymethod-pass"
                  >
                    Class Pass
                  </button>
                )}
              </div>
              {selectedClass?.is_collab && (
                <p className="text-xs text-zinc-500 mt-2" data-testid="collab-pass-note">This is a collab class — Class Passes can't be used. Please pay via GCash or bank transfer.</p>
              )}
              {!selectedClass?.is_collab && payMethod === 'pass' && (
                <div className="mt-3">
                  <Label htmlFor="pass_code">Class Pass Code *</Label>
                  <Input
                    id="pass_code"
                    value={passCode}
                    onChange={(e) => setPassCode(e.target.value.toUpperCase())}
                    placeholder="PASS-XXXX-XXXX"
                    className="font-mono tracking-wider"
                    data-testid="booking-pass-code-input"
                  />
                  <p className="text-xs text-zinc-500 mt-1">1 credit will be used. Your slot confirms instantly — no payment needed.</p>
                </div>
              )}
            </div>

            {/* Booking Terms */}
            <div className="pt-2 border-t border-zinc-200" data-testid="booking-terms">
              <p className="font-bold text-sm mb-1" style={{ fontFamily: 'var(--heading-font)' }}>Booking Terms</p>
              <p className="text-xs text-zinc-500 mb-3">By proceeding with your booking, please acknowledge and agree to the following terms:</p>
              <div className="space-y-3">
                <label className="flex gap-3 items-start cursor-pointer" data-testid="term-payment-label">
                  <input type="checkbox" checked={terms.payment} onChange={(e) => setTerms({ ...terms, payment: e.target.checked })} className="mt-1 w-4 h-4 accent-black shrink-0" data-testid="term-payment" />
                  <span className="text-xs text-zinc-700"><strong>Payment &amp; Rescheduling.</strong> All payments are strictly non-refundable. Bookings may be rescheduled to a future class if requested at least 1 hour prior to the start time. Late requests and no-shows forfeit the booking fee.</span>
                </label>
                <label className="flex gap-3 items-start cursor-pointer" data-testid="term-media-label">
                  <input type="checkbox" checked={terms.media} onChange={(e) => setTerms({ ...terms, media: e.target.checked })} className="mt-1 w-4 h-4 accent-black shrink-0" data-testid="term-media" />
                  <span className="text-xs text-zinc-700"><strong>Media &amp; Photography Release.</strong> Classes may be photographed or filmed for promotional and social media use. If you prefer not to be recorded, please inform studio staff or your instructor prior to class.</span>
                </label>
                <label className="flex gap-3 items-start cursor-pointer" data-testid="term-property-label">
                  <input type="checkbox" checked={terms.property} onChange={(e) => setTerms({ ...terms, property: e.target.checked })} className="mt-1 w-4 h-4 accent-black shrink-0" data-testid="term-property" />
                  <span className="text-xs text-zinc-700"><strong>Personal Property.</strong> The Studio is not liable for lost, stolen, or damaged personal items or property left on the premises. Please secure your valuables.</span>
                </label>
                <label className="flex gap-3 items-start cursor-pointer" data-testid="term-health-label">
                  <input type="checkbox" checked={terms.health} onChange={(e) => setTerms({ ...terms, health: e.target.checked })} className="mt-1 w-4 h-4 accent-black shrink-0" data-testid="term-health" />
                  <span className="text-xs text-zinc-700"><strong>Health &amp; Liability Waiver.</strong> Dance instruction carries inherent risks of physical strain or injury. By booking, you confirm you are physically fit to participate and agree that the Studio and its instructors are not liable for any injuries, illness, or health hazards incurred.</span>
                </label>
              </div>
            </div>

            <div className="pt-4 space-y-2">
              <button
                type="submit"
                disabled={!(terms.payment && terms.media && terms.property && terms.health)}
                className="w-full bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)] rounded-full px-6 py-3 font-bold transition-transform hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                data-testid="confirm-booking-btn"
              >
                {payMethod === 'pass' ? 'CONFIRM WITH CLASS PASS' : 'PROCEED TO PAYMENT'}
              </button>
              <button
                type="button"
                onClick={() => setSelectedClass(null)}
                className="w-full border-2 border-black text-black hover:bg-black hover:text-white rounded-full px-6 py-3 transition-colors"
                data-testid="cancel-booking-btn"
              >
                CANCEL
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Classes;